// client/hooks/useSocket.js
// ✅ React Hook für Socket.io
import { useEffect, useState, useCallback, useRef } from "react";
import {
  connectSocket,
  disconnectSocket,
  onSocketEvent,
  isConnected,
  SOCKET_EVENTS,
} from "../utils/socket.js";

/**
 * Hook für Socket.io Verbindung
 * Verbindet automatisch bei Mount und trennt bei Unmount
 */
export function useSocketConnection() {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const socket = connectSocket();

    const handleConnect = () => setConnected(true);
    const handleDisconnect = () => setConnected(false);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    // Initial Status setzen
    setConnected(socket.connected);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      // Nicht disconnecten - andere Komponenten könnten es noch brauchen
    };
  }, []);

  return connected;
}

/**
 * Hook für Socket-Events
 * @param {string} event - Event Name
 * @param {Function} callback - Callback bei Event
 */
export function useSocketEvent(event, callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler = (data) => {
      callbackRef.current?.(data);
    };

    const unsubscribe = onSocketEvent(event, handler);
    return unsubscribe;
  }, [event]);
}

/**
 * Hook für neue Benachrichtigungen
 */
export function useNotificationSocket(onNewNotification) {
  useSocketEvent(SOCKET_EVENTS.NOTIFICATION_NEW, onNewNotification);
}

/**
 * Hook für Pendenz-Updates
 */
export function usePendenzSocket({ onCreated, onUpdated, onAssigned }) {
  useSocketEvent(SOCKET_EVENTS.PENDENZ_CREATED, onCreated);
  useSocketEvent(SOCKET_EVENTS.PENDENZ_UPDATED, onUpdated);
  useSocketEvent(SOCKET_EVENTS.PENDENZ_ASSIGNED, onAssigned);
}

/**
 * Hook für Projekt-Updates
 */
export function useProjectSocket({ onUpdated, onComment }) {
  useSocketEvent(SOCKET_EVENTS.PROJECT_UPDATED, onUpdated);
  useSocketEvent(SOCKET_EVENTS.PROJECT_COMMENT, onComment);
}

/**
 * Hook für System-Ankündigungen
 */
export function useSystemAnnouncement(callback) {
  useSocketEvent(SOCKET_EVENTS.SYSTEM_ANNOUNCEMENT, callback);
}

export { SOCKET_EVENTS };

export default useSocketConnection;
